import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Home, LayoutDashboard, Mail, LogOut, ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { useState } from "react";
import { FNCLogo } from "./FNCLogo";

interface SidebarProps {
  className?: string;
}

export const Sidebar: React.FC<SidebarProps> = ({ className }) => {
  const { pathname } = useLocation();
  const { logout, user } = useAuth();
  const [collapsed, setCollapsed] = useState(false);

  const menuItems = [
    { label: "Inicio", icon: Home, path: "/home" },
    { label: "Dashboard", icon: LayoutDashboard, path: "/dashboard" },
    { label: "Contacto", icon: Mail, path: "/contacto" },
  ];

  return (
    <aside
      className={cn(
        "flex flex-col bg-white border-r border-gray-100 transition-all duration-300 h-screen sticky top-0",
        collapsed ? "w-20" : "w-64",
        className
      )}
    >
      {/* Header / Logo */}
      <div className="p-6 flex flex-col items-center border-b border-gray-50 mb-4 min-h-[220px] justify-between gap-6">
        {!collapsed && (
          <div className="w-full flex justify-center mb-2">
            <FNCLogo variant="dark" className="h-24 w-auto object-contain" />
          </div>
        )}
        <div className="flex items-center justify-between w-full mt-auto">
          {!collapsed && (
            <div className="flex flex-col">
              <span className="font-montserrat text-xl font-bold text-[#80142D] leading-tight">
                Dashboard
              </span>
              <span className="text-xs text-gray-400 uppercase tracking-widest font-semibold">
                Institucional
              </span>
            </div>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setCollapsed(!collapsed)}
            className="text-gray-400 hover:text-[#80142D] hover:bg-red-50"
          >
            {collapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
          </Button>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-4 space-y-1">
        {menuItems.map((item) => {
          const isActive = pathname === item.path;
          return (
            <Link
              key={item.path}
              to={item.path}
              className={cn(
                "flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 group",
                isActive
                  ? "bg-red-50 text-[#80142D] font-semibold"
                  : "text-gray-500 hover:bg-gray-50 hover:text-gray-900"
              )}
            >
              <item.icon
                size={22}
                className={cn(
                  "transition-colors",
                  isActive ? "text-[#80142D]" : "text-gray-400 group-hover:text-gray-600"
                )}
              />
              {!collapsed && <span>{item.label}</span>}
            </Link>
          );
        })}
      </nav>

      {/* Footer / Logout */}
      <div className="p-4 border-t border-gray-50">
        {!collapsed && user && (
          <div className="px-4 py-3 mb-2">
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">Usuario</p>
            <p className="text-sm font-semibold text-gray-700 truncate">{user.usuario}</p>
            <p className="text-[10px] text-gray-400">{user.rol}</p>
          </div>
        )}
        
        {!collapsed && (
          <div className="px-4 mb-4">
            <p className="text-[10px] text-gray-400 uppercase tracking-widest font-semibold flex flex-col">
              <span>Desarrollado por</span>
              <span className="text-black">EMDECOB</span>
            </p>
          </div>
        )}

        <button
          onClick={logout}
          className={cn(
            "flex items-center gap-3 w-full px-4 py-3 text-red-500 hover:bg-red-50 rounded-xl transition-all duration-200",
            collapsed && "justify-center"
          )}
        >
          <LogOut size={22} />
          {!collapsed && <span className="font-medium">Cerrar sesión</span>}
        </button>
      </div>
    </aside>
  );
};
